import { useState } from 'react';
import {
  Button,
  Tag,
  Toggletip,
  ToggletipButton,
  ToggletipContent,
} from '@carbon/react';
import {
  Add,
  ChevronDown,
  ChevronRight,
  Close,
  Copy,
  Edit,
} from '@carbon/react/icons';
import { ColorSwatch, hexToRgba } from 'components/color-picker';
import {
  ColorByAttributeModal,
  type ColorByCategory,
  type ColorByOption,
  type ColorByRule,
} from 'components/color-by-attribute-modal';
import { format0 } from 'utils/number';
import styles from './cross-section-rail-section.module.scss';

/** How many of a filter's values the rail shows before it starts counting them. */
const VISIBLE_FILTER_VALUES = 1;

/**
 * The values a filter keeps: the first, then a count of the rest that opens them.
 *
 * A filter over twenty operators printed twenty pills across a 340px rail, which
 * overlapped into one illegible run of text. One value and a count is the overflow
 * pattern the rest of the product uses, and the count is a button rather than a hint
 * because the values behind it are the answer to "what is this filtering to".
 *
 * @param {object} props - The attribute's name, and the values kept.
 * @return {JSX.Element} The pills.
 */
const FilterValueTags = ({
  label,
  values,
}: {
  label: string;
  values: string[];
}): JSX.Element => {
  const shown = values.slice(0, VISIBLE_FILTER_VALUES);
  const rest = values.slice(VISIBLE_FILTER_VALUES);

  return (
    <span className={styles.filterTags}>
      {shown.map((value) => (
        <Tag key={value} type="gray" size="sm" title={value}>
          {value}
        </Tag>
      ))}
      {rest.length > 0 && (
        <Toggletip align="bottom-start" className={styles.filterOverflow}>
          <ToggletipButton label={`Show the other ${label} values`}>
            <Tag type="gray" size="sm">{`+${format0(rest.length)}`}</Tag>
          </ToggletipButton>
          <ToggletipContent>
            <p className={styles.filterOverflowTitle}>{label}</p>
            <ul className={styles.filterOverflowList}>
              {rest.map((value) => (
                <li key={value}>{value}</li>
              ))}
            </ul>
          </ToggletipContent>
        </Toggletip>
      )}
    </span>
  );
};

/**
 * One filter as the rail renders it: an attribute and the values kept. Structural, so
 * a dataset's own filter type satisfies it without the rail knowing that type.
 */
export interface RailFilter {
  id: string;
  attribute: string;
  values: string[];
  labels: string[];
  enabled: boolean;
}

/** Which control opened the modal, and the rule or filter it should open on. */
interface ModalState {
  mode: 'filter' | 'color';
  focusId: string | null;
}

export interface RailDatasetSectionProps {
  /** The dataset's name, as the rail heads it. */
  title: string;
  /** Shown beside the title when there is something to count. */
  count?: number;
  /** True when the dataset has anything on the section to colour. */
  hasItems: boolean;
  /** Said in place of the colour empty-line when the dataset itself is empty. */
  emptyMessage: string;
  rules: ColorByRule[];
  onRulesChange: (next: ColorByRule[]) => void;
  /** The attributes a rule can be written over. */
  categories: ColorByCategory[];
  /** The surfaces a rule can paint. */
  targets: Array<{ id: string; label: string }>;
  /** The values an attribute takes across what is on the section. */
  getOptions: (categoryId: string) => ColorByOption[];
  /** Re-derives a rule against what is on the section now. */
  refresh: (rule: ColorByRule) => ColorByRule;
  /** Apply-To defaults for a new rule. */
  applyTo: Record<string, boolean>;
  /**
   * The dataset's filters. Present makes the section filterable — Structure Grids
   * are not, because the section's grids are chosen in settings rather than
   * narrowed here.
   */
  filters?: RailFilter[];
  onFiltersChange?: (next: RailFilter[]) => void;
  /**
   * The values a filter can choose from. Derived from the dataset BEFORE filtering,
   * so a filter can always be widened again — options taken from filtered data trap
   * the user in the first choice they make.
   */
  getFilterOptions?: (categoryId: string) => ColorByOption[];
  /**
   * False hides "Color by Attribute" for a host that keeps one such button of its
   * own: the rules already here stay editable.
   */
  canAddRules?: boolean;
}

/**
 * One dataset in the display rail, in the pattern every dataset section in the app
 * follows: a heading that expands, then per concern an action that adds one and the
 * cards it added — filters, then colour-by rules with a swatch per derived value.
 *
 * The action comes before its cards, and stays put whether or not there are any: it
 * is how the first one gets added, so hiding it when there is nothing to show is the
 * one time it is certainly needed.
 *
 * Structure Grids and Wells are two configurations of this rather than two
 * components, because in the rail they are the same control with different
 * vocabularies — which is the point of the pattern.
 *
 * @param {RailDatasetSectionProps} props - Component props.
 * @return {JSX.Element} The section.
 */
export const RailDatasetSection = ({
  title,
  count,
  hasItems,
  emptyMessage,
  rules,
  onRulesChange,
  categories,
  targets,
  getOptions,
  refresh,
  applyTo,
  filters,
  onFiltersChange,
  getFilterOptions,
  canAddRules = true,
}: RailDatasetSectionProps): JSX.Element => {
  const [open, setOpen] = useState(true);
  const [modal, setModal] = useState<ModalState | null>(null);

  const filterable = filters !== undefined && onFiltersChange !== undefined;

  /**
   * A swatch edit. The picker reports colour and opacity separately, and both are
   * kept — stored as `rgba()`, the way the Type Curve rail stores its rules, so a
   * partly transparent choice survives a reload and reads the same everywhere.
   *
   * @param {string} ruleId - The rule the value belongs to.
   * @param {string} value - The derived value being recoloured.
   * @param {string} color - The hex colour picked.
   * @param {number} opacity - The opacity picked, 0-100.
   */
  const setValueColor = (
    ruleId: string,
    value: string,
    color: string,
    opacity: number
  ) => {
    onRulesChange(
      rules.map((rule) => {
        if (rule.ruleId !== ruleId) {
          return rule;
        }
        return {
          ...rule,
          valueColors: {
            ...rule.valueColors,
            [value]: hexToRgba(color, opacity),
          },
        };
      })
    );
  };

  /**
   * Copies a rule, so a second one can be built from it rather than from scratch —
   * the same affordance the Type Curve rail gives its colour rules.
   *
   * @param {ColorByRule} rule - The rule to copy.
   */
  const duplicateRule = (rule: ColorByRule) => {
    onRulesChange([
      ...rules,
      {
        ...rule,
        ruleId: `${rule.attribute}-${rules.length + 1}-${
          rule.selectedValues.length
        }`,
        valueColors: { ...rule.valueColors },
        applyTo: { ...rule.applyTo },
        enabled: true,
      },
    ]);
  };

  /**
   * The filters in the shape the shared modal reads them. A filter is a rule without
   * colours or surfaces, which is exactly what the modal's filter mode edits.
   *
   * @return {ColorByRule[]} The filters as modal rules.
   */
  const filtersAsRules = (): ColorByRule[] =>
    (filters ?? []).map((filter) => ({
      ruleId: filter.id,
      attribute: filter.attribute,
      selectedValues: filter.values,
      selectedValueLabels: filter.labels,
      valueColors: {},
      applyTo: {},
      enabled: filter.enabled,
    }));

  /**
   * The filters back from what the modal applied, keeping each filter's enabled state
   * — Apply edits a selection, it does not switch a disabled filter back on.
   *
   * @param {ColorByRule[]} next - What the modal emitted.
   */
  const applyFilters = (next: ColorByRule[]) => {
    onFiltersChange?.(
      next.map((rule) => ({
        id: rule.ruleId,
        attribute: rule.attribute,
        values: rule.selectedValues,
        labels: rule.selectedValueLabels,
        enabled:
          (filters ?? []).find((filter) => filter.id === rule.ruleId)
            ?.enabled ?? true,
      }))
    );
  };

  /**
   * How the rail names an attribute, falling back to its id when the vocabulary has
   * no label for it.
   *
   * @param {string} attribute - The attribute id.
   * @return {string} The label to show.
   */
  const attributeLabel = (attribute: string): string => {
    const match = categories.find((category) => category.id === attribute);
    return match?.label ?? attribute;
  };

  return (
    <>
      <section className={styles.railSection}>
        <button
          type="button"
          className={styles.head}
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <span>{title}</span>
          {count !== undefined && count > 0 && (
            <span className={styles.count}>{`(${format0(count)})`}</span>
          )}
        </button>

        {open && (
          <div className={styles.section}>
            {filterable && (
              <div className={styles.block}>
                <div className={styles.addRule}>
                  <Button
                    kind="tertiary"
                    size="sm"
                    renderIcon={Add}
                    onClick={() => setModal({ mode: 'filter', focusId: null })}
                  >
                    Add Filter
                  </Button>
                </div>
                {filters?.length === 0 ? (
                  <p className={styles.empty}>No filters added.</p>
                ) : (
                  filters?.map((filter) => (
                    <div key={filter.id} className={styles.card}>
                      <div className={styles.cardHeader}>
                        <span className={styles.cardLabel}>
                          {attributeLabel(filter.attribute)}
                        </span>
                        <div className={styles.cardActions}>
                          <Button
                            kind="ghost"
                            size="sm"
                            hasIconOnly
                            renderIcon={Edit}
                            iconDescription={`Edit ${attributeLabel(
                              filter.attribute
                            )} filter`}
                            tooltipPosition="left"
                            onClick={() =>
                              setModal({ mode: 'filter', focusId: filter.id })
                            }
                          />
                          <Button
                            kind="ghost"
                            size="sm"
                            hasIconOnly
                            renderIcon={Close}
                            iconDescription={`Remove ${attributeLabel(
                              filter.attribute
                            )} filter`}
                            tooltipPosition="left"
                            onClick={() =>
                              onFiltersChange?.(
                                (filters ?? []).filter(
                                  (item) => item.id !== filter.id
                                )
                              )
                            }
                          />
                        </div>
                      </div>
                      <div className={styles.cardControls}>
                        <FilterValueTags
                          label={attributeLabel(filter.attribute)}
                          values={filter.labels}
                        />
                        <Button
                          kind="ghost"
                          size="sm"
                          // A colour rule offers the same word in the same section,
                          // so the accessible name says which one this is. It keeps
                          // the visible label inside it, per label-in-name.
                          aria-label={`${
                            filter.enabled ? 'Disable' : 'Enable'
                          } ${attributeLabel(filter.attribute)} filter`}
                          onClick={() =>
                            onFiltersChange?.(
                              (filters ?? []).map((item) => {
                                if (item.id !== filter.id) {
                                  return item;
                                }
                                return { ...item, enabled: !item.enabled };
                              })
                            )
                          }
                        >
                          {filter.enabled ? 'Disable' : 'Enable'}
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            <div className={styles.block}>
              {canAddRules && (
                <div className={styles.addRule}>
                  <Button
                    kind="tertiary"
                    size="sm"
                    renderIcon={Add}
                    onClick={() => setModal({ mode: 'color', focusId: null })}
                  >
                    Color by Attribute
                  </Button>
                </div>
              )}

              {rules.length === 0 ? (
                // When the dataset itself is empty, say that instead — it is the more
                // useful of the two facts, and it is why there is nothing to colour.
                <p className={styles.empty}>
                  {hasItems ? 'No color rules added.' : emptyMessage}
                </p>
              ) : (
                rules.map((rule) => {
                  const painted = targets.filter(
                    (target) => rule.applyTo[target.id]
                  );
                  return (
                    <div key={rule.ruleId} className={styles.card}>
                      <div className={styles.cardHeader}>
                        <span className={styles.cardLabel}>
                          {attributeLabel(rule.attribute)}
                        </span>
                        {/* A rule kept but not applied. Said on the card rather
                            than only in the button that undoes it: the card is
                            what the user reads when the swatches no longer match
                            the plot. */}
                        {!rule.enabled && (
                          <Tag
                            type="cool-gray"
                            size="sm"
                            className={styles.statePill}
                          >
                            Disabled
                          </Tag>
                        )}
                        <div className={styles.cardActions}>
                          <Button
                            kind="ghost"
                            size="sm"
                            hasIconOnly
                            renderIcon={Edit}
                            iconDescription={`Edit ${title} colours`}
                            tooltipPosition="left"
                            onClick={() =>
                              setModal({ mode: 'color', focusId: rule.ruleId })
                            }
                          />
                          <Button
                            kind="ghost"
                            size="sm"
                            hasIconOnly
                            renderIcon={Copy}
                            iconDescription="Duplicate rule"
                            tooltipPosition="left"
                            onClick={() => duplicateRule(rule)}
                          />
                          <Button
                            kind="ghost"
                            size="sm"
                            hasIconOnly
                            renderIcon={Close}
                            iconDescription={`Reset ${title} colours`}
                            tooltipPosition="left"
                            onClick={() =>
                              onRulesChange(
                                rules.filter(
                                  (item) => item.ruleId !== rule.ruleId
                                )
                              )
                            }
                          />
                        </div>
                      </div>

                      <div className={styles.cardControls}>
                        {painted.length > 0 && (
                          <span className={styles.activeOn}>
                            <span className={styles.activeOnLabel}>
                              Active on:
                            </span>
                            {painted.map((target) => (
                              <Tag key={target.id} type="gray" size="sm">
                                {target.label}
                              </Tag>
                            ))}
                          </span>
                        )}
                        <Button
                          kind="ghost"
                          size="sm"
                          onClick={() =>
                            onRulesChange(
                              rules.map((item) => {
                                if (item.ruleId !== rule.ruleId) {
                                  return item;
                                }
                                return { ...item, enabled: !item.enabled };
                              })
                            )
                          }
                        >
                          {rule.enabled ? 'Disable' : 'Enable'}
                        </Button>
                      </div>

                      {/* The derived values, each recolourable where it is read. */}
                      <div className={styles.values}>
                        {rule.selectedValues.map((value) => (
                          <div key={value} className={styles.valueRow}>
                            <ColorSwatch
                              color={rule.valueColors[value]}
                              onChange={(color, opacity) =>
                                setValueColor(
                                  rule.ruleId,
                                  value,
                                  color,
                                  opacity
                                )
                              }
                            />
                            <span className={styles.valueName}>{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </section>

      {modal?.mode === 'color' && (
        <ColorByAttributeModal
          open
          onClose={() => setModal(null)}
          editingRuleId={modal.focusId}
          rules={rules}
          onApply={(next) => {
            onRulesChange(next.map(refresh));
            setModal(null);
          }}
          categories={categories}
          getOptions={getOptions}
          applyToTargets={targets}
          newRuleApplyTo={applyTo}
          resetApplyTo={applyTo}
          title="Color by Attribute"
        />
      )}

      {modal?.mode === 'filter' && (
        <ColorByAttributeModal
          open
          mode="filter"
          onClose={() => setModal(null)}
          editingRuleId={modal.focusId}
          rules={filtersAsRules()}
          onApply={(next) => {
            applyFilters(next);
            setModal(null);
          }}
          categories={categories}
          getOptions={getFilterOptions ?? getOptions}
          applyToTargets={[]}
          newRuleApplyTo={{}}
          resetApplyTo={{}}
          title="Add Filter"
        />
      )}
    </>
  );
};
